import { PipelineEditor } from "./PipelineEditor";
import { seenLabel } from "../lib/radar";
import { ArrowUpRight, Banknote, BriefcaseBusiness, Clock3, MapPin, X } from "lucide-react";
import { formatAge, formatLocation, formatSalary } from "../lib/jobs";
import type { Job } from "../types/job";

interface JobDetailProps {
  job: Job | null;
  onClose: () => void;
  onSaved: () => void;
}
export function JobDetail({ job, onClose, onSaved }: JobDetailProps) {
  if (!job) return null;

  return (
    <aside className="job-detail" aria-live="polite" aria-label={`Selected job: ${job.title}`}>
      <div className="detail-topline">
        <span className={`freshness freshness-${job.freshness}`}><i />{job.radar?.freshnessBand ?? job.freshness}</span>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close job details">
          <X size={17} />
        </button>
      </div>
      <div className="detail-company">{job.company}</div>
      <h2>{job.title}</h2>

      <dl className="job-facts">
        <div><dt><MapPin size={15} />Location</dt><dd>{formatLocation(job)}</dd></div>
        <div><dt><Banknote size={15} />Compensation</dt><dd>{formatSalary(job)}</dd></div>
        <div><dt><Clock3 size={15} />Posted</dt><dd>{job.postedAt ? formatAge(job.ageHours) : "Unknown"}</dd></div>
        <div><dt><BriefcaseBusiness size={15} />Source</dt><dd>{job.source}</dd></div>
      </dl>

      {job.radar && <section className="radar-evidence">
        <p className="radar-score">{job.radar.score}/100 priority · {job.radar.components.fit}/100 fit</p>
        <p>{seenLabel(job)}</p>
        <p><strong>Resume: {job.radar.resumeTrack}</strong></p>
        <ul>{job.radar.reasons.map(reason => <li key={reason}>{reason}</li>)}</ul>
        <details><summary>Score evidence & unknowns</summary>
          <p>40% fit · 25% freshness · 20% human access · 15% hiring speed</p>
          <p>Component scores: {job.radar.components.fit} / {job.radar.components.freshness} / {job.radar.components.accessibility} / {job.radar.components.hiringSpeed}</p>
          {job.radar.penalties.map(p => <p key={p.reason}>−{p.points}: {p.reason}</p>)}
          {job.radar.unknowns.map(u => <p key={u}>{u}</p>)}
          {job.radar.speedEvidence && <p>Listing says: “{job.radar.speedEvidence}”</p>}
        </details>
        <p><strong>Human next step</strong><br />{job.radar.outreachAction}</p>
      </section>}
      <a className="view-job-button" href={job.radar?.directApplyUrl ?? job.applicationUrl} target="_blank" rel="noreferrer">
        {job.radar?.directApplyUrl ? "Open employer application" : "Review source listing"} <ArrowUpRight size={16} />
      </a>
      <p className="detail-footnote">Opening a listing does not submit an application.</p>
      <PipelineEditor job={job} onSaved={onSaved} />
    </aside>
  );
}
